import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { MatDialog } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import { ToastrService } from "../shared/toastr.service";
import { catchError, map, mergeMap, switchMap, tap } from "rxjs/operators";
import { of } from "rxjs";
import { LimitPlanDialogComponent } from "../shared/limit-plan-dialog/limit-plan-dialog.component";
import {
  CHANGE_SIGNATURE,
  ChangeSignatureAction,
  ChangeSignatureFailureAction,
  ChangeSignatureSuccessAction,
  DUPLICATE_SIGNATURE,
  DUPLICATE_SIGNATURE_SUCCESS,
  DUPLICATE_SIGNATURE_FAILURE,
  DuplicateSignatureAction,
  DuplicateSignatureFailureAction,
  DuplicateSignatureSuccessAction,
  GET_SIGNATURES,
  GetSignaturesAction,
  GetSignaturesFailureAction,
  GetSignaturesSuccessAction,
  INSTALL_SIGNATURE,
  INSTALL_SIGNATURE_SUCCESS,
  InstallSignatureAction,
  InstallSignatureFailureAction,
  InstallSignatureSuccessAction,
  REMOVE_SIGNATURE,
  REMOVE_SIGNATURE_SUCCESS,
  REINSTALL_SIGNATURES,
  ReinstallSignaturesAction,
  ReinstallSignaturesFailureAction,
  ReinstallSignaturesSuccessAction,
  RemoveSignatureAction,
  RemoveSignatureFailureAction,
  RemoveSignatureSuccessAction,
  SET_DEFAULT_SIGNATURE,
  SetDefaultSignatureAction,
  SetDefaultSignatureFailureAction,
  SetDefaultSignatureSuccessAction,
  UNINSTALL_SIGNATURE,
  UninstallSignatureAction,
  UninstallSignatureFailureAction,
  UninstallSignatureSuccessAction,
  REORDER_SIGNATURES,
  ReorderSignaturesAction,
  ReorderSignaturesSuccessAction,
  ReorderSignaturesFailureAction,
  REORDER_SIGNATURES_SUCCESS,
} from "./signatures.actions";
import { SignaturesService } from "./signatures.service";
import { ChangeSignatureObject, Signature } from "./signatures.interface";
import { scrollToBottom } from "../shared/utils/window.utils";
import { FirstInstallationCongratulationsDialogComponent } from "./first-installation-congratulations-dialog/first-installation-congratulations-dialog.component";

@Injectable()
export class SignaturesEffects {
  getSignatures$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(GET_SIGNATURES),
      switchMap((action: GetSignaturesAction) => {
        return this.signaturesService.getSignatures(action.all).pipe(
          switchMap((response: Signature[]) => {
            return [new GetSignaturesSuccessAction(response)];
          }),
          catchError((error) => of(new GetSignaturesFailureAction(error))),
        );
      }),
    );
  });

  changeSignature$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CHANGE_SIGNATURE),
      map((action: ChangeSignatureAction) => action.payload),
      switchMap((payload: ChangeSignatureObject) => {
        return this.signaturesService.changeSignature().pipe(
          switchMap(() => {
            return [new ChangeSignatureSuccessAction(payload)];
          }),
          catchError((error) => of(new ChangeSignatureFailureAction(error))),
        );
      }),
    ),
  );

  removeSignature$ = createEffect(() =>
    this.actions$.pipe(
      ofType(REMOVE_SIGNATURE),
      map((action: RemoveSignatureAction) => action.payload),
      switchMap((payload: string) => {
        return this.signaturesService.removeSignature(payload).pipe(
          switchMap(() => {
            return [new RemoveSignatureSuccessAction(payload)];
          }),
          catchError((error) => of(new RemoveSignatureFailureAction(error))),
        );
      }),
    ),
  );

  installSignature$ = createEffect(() =>
    this.actions$.pipe(
      ofType(INSTALL_SIGNATURE),
      map((action: InstallSignatureAction) => action.payload),
      switchMap((payload: unknown) => {
        const payloadRecord = payload as Record<string, unknown>;
        const payloadId =
          typeof payload === "string" ? payload : JSON.stringify(payload);
        return this.signaturesService.installSignature(payloadRecord).pipe(
          switchMap((response) => {
            return [
              new InstallSignatureSuccessAction(
                payloadId,
                response.isFirstInstallation,
              ),
            ];
          }),
          catchError((error) => of(new InstallSignatureFailureAction(error))),
        );
      }),
    ),
  );

  uninstallSignature$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UNINSTALL_SIGNATURE),
      map((action: UninstallSignatureAction) => action.payload),
      switchMap((payload: string) => {
        return this.signaturesService.uninstallSignature().pipe(
          switchMap(() => {
            return [new UninstallSignatureSuccessAction(payload)];
          }),
          catchError((error) => of(new UninstallSignatureFailureAction(error))),
        );
      }),
    ),
  );

  setDefaultSignature$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SET_DEFAULT_SIGNATURE),
      map((action: SetDefaultSignatureAction) => action.payload),
      switchMap((payload: string) => {
        return this.signaturesService.setDefaultForNewEmployee(payload).pipe(
          switchMap(() => {
            return [
              new GetSignaturesAction(),
              new SetDefaultSignatureSuccessAction(),
            ];
          }),
          catchError((error) =>
            of(new SetDefaultSignatureFailureAction(error)),
          ),
        );
      }),
    ),
  );

  duplicateSignature$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(DUPLICATE_SIGNATURE),
      map((action: DuplicateSignatureAction) => action.payload),
      switchMap((payload: string) => {
        return this.signaturesService.duplicateSignature(payload).pipe(
          switchMap((response: { templateId: string }) => {
            return [new DuplicateSignatureSuccessAction(response.templateId)];
          }),
          catchError((error) => of(new DuplicateSignatureFailureAction(error))),
        );
      }),
    );
  });

  duplicateSignatureSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DUPLICATE_SIGNATURE_SUCCESS),
        tap(() => {
          this.toastr.success(
            this.translate.instant("signatures.duplicateSuccess") as string,
          );
          scrollToBottom();
        }),
      ),
    { dispatch: false },
  );

  duplicateSignatureFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DUPLICATE_SIGNATURE_FAILURE),
        tap((action: DuplicateSignatureFailureAction) => {
          const payload = action.payload as
            | { error?: { message?: string }; message?: string }
            | { error?: string }
            | string
            | undefined;
          const errorMessage =
            (typeof payload === "object" &&
              payload !== null &&
              ("error" in payload
                ? typeof payload.error === "object" && payload.error !== null
                  ? "message" in payload.error
                    ? payload.error.message
                    : undefined
                  : typeof payload.error === "string"
                    ? payload.error
                    : undefined
                : "message" in payload
                  ? payload.message
                  : undefined)) ||
            (typeof payload === "string" ? payload : "") ||
            "";
          if (
            typeof errorMessage === "string" &&
            (errorMessage.includes("TEMPLATE_LIMIT_REACHED") ||
              errorMessage.includes("Template limit reached"))
          ) {
            this.dialog.open(LimitPlanDialogComponent, {
              width: "500px",
              maxWidth: "90vw",
              data: { plan: "basic", type: "template" },
            });
          } else {
            this.toastr.error(
              this.translate.instant("footers.duplicateError") as string,
            );
          }
        }),
      ),
    { dispatch: false },
  );

  removeSignatureSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(REMOVE_SIGNATURE_SUCCESS),
        tap(() => {
          this.toastr.success(
            this.translate.instant("signatures.deleteSuccess") as string,
          );
        }),
      ),
    { dispatch: false },
  );

  reinstallSignatures$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ReinstallSignaturesAction>(REINSTALL_SIGNATURES),
      mergeMap(() =>
        this.signaturesService.reinstallSignatures().pipe(
          map(() => {
            this.toastr.success(
              this.translate.instant("signatures.reinstallSuccess") as string,
            );
            return new ReinstallSignaturesSuccessAction();
          }),
          catchError((error) =>
            of(new ReinstallSignaturesFailureAction(error)),
          ),
        ),
      ),
    ),
  );

  reorderSignatures$ = createEffect(() =>
    this.actions$.pipe(
      ofType(REORDER_SIGNATURES),
      map((action: ReorderSignaturesAction) => action.payload),
      switchMap((ids) =>
        this.signaturesService.reorderSignatures(ids).pipe(
          map(() => new ReorderSignaturesSuccessAction(ids)),
          catchError((err) => of(new ReorderSignaturesFailureAction(err))),
        ),
      ),
    ),
  );

  reorderSignaturesSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(REORDER_SIGNATURES_SUCCESS),
      map(() => new GetSignaturesAction()),
    ),
  );

  showFirstInstallationDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(INSTALL_SIGNATURE_SUCCESS),
        tap((action: InstallSignatureSuccessAction) => {
          if (action.isFirstInstallation) {
            this.dialog.open(FirstInstallationCongratulationsDialogComponent, {
              width: "500px",
              disableClose: true,
              panelClass: "app-dialog-panel",
            });
          }
        }),
      ),
    { dispatch: false },
  );

  constructor(
    private actions$: Actions,
    private signaturesService: SignaturesService,
    private translate: TranslateService,
    private toastr: ToastrService,
    private dialog: MatDialog,
  ) {}
}
